import { useState } from "react";
import type { TimeDayBlock } from "../api/types";
import { formatMinutes } from "../lib/format";
import { minToLabel } from "../lib/timelineSlots";
import { ReimputeSheet, type ReimputeTarget } from "./ReimputeSheet";

/** Écart toléré entre deux tranches pour les lire comme une seule. */
const JOIN_GAP_MIN = 5;

interface BlockGroup {
  start_minute: number;
  end_minute: number;
  worked_minutes: number;
  activity_code: string;
  project_id?: string;
  project_label?: string;
  count: number;
  eventIds: string[];
}

/** Tranches qui se suivent sur le même projet (ou toutes deux sans projet) :
 * une seule ligne. Le kit découpe une journée en dizaines de tranches de
 * quelques minutes, illisibles une par une. */
function groupTimeBlocks(blocks: TimeDayBlock[]): BlockGroup[] {
  const groups: BlockGroup[] = [];
  for (const b of [...blocks].sort((x, y) => x.start_minute - y.start_minute)) {
    const last = groups[groups.length - 1];
    if (
      last &&
      last.project_id === b.project_id &&
      b.start_minute - last.end_minute <= JOIN_GAP_MIN
    ) {
      last.end_minute = Math.max(last.end_minute, b.end_minute);
      last.worked_minutes += b.end_minute - b.start_minute;
      last.count += 1;
      if (last.activity_code !== b.activity_code) last.activity_code = "";
      if (b.start_event_id) last.eventIds.push(b.start_event_id);
      continue;
    }
    groups.push({
      start_minute: b.start_minute,
      end_minute: b.end_minute,
      worked_minutes: b.end_minute - b.start_minute,
      activity_code: b.activity_code,
      project_id: b.project_id,
      project_label: b.project_label,
      count: 1,
      eventIds: b.start_event_id ? [b.start_event_id] : [],
    });
  }
  return groups;
}

/** Tranches d'une journée, regroupées quand elles se suivent sur le même
 * projet, avec l'action d'imputer (sans projet) ou de changer de projet.
 * `only` restreint l'affichage après regroupement. Sans `editable`, lecture
 * seule. Une tranche sans activity_start (TAWA antérieur) n'a pas d'action. */
export function TimeBlockList({
  blocks,
  editable,
  labels,
  only,
  ariaLabel = "Créneaux et projets",
}: {
  blocks: TimeDayBlock[];
  editable: boolean;
  labels?: Map<string, string>;
  only?: "assigned" | "unassigned";
  ariaLabel?: string;
}) {
  const [targets, setTargets] = useState<ReimputeTarget[] | null>(null);
  const groups = groupTimeBlocks(blocks).filter(
    (g) => !only || (only === "assigned" ? !!g.project_id : !g.project_id),
  );
  if (groups.length === 0) return null;

  return (
    <>
      <ul className="time-blocks" aria-label={ariaLabel}>
        {groups.map((g) => {
          const range = `${minToLabel(g.start_minute)} à ${minToLabel(g.end_minute)}`;
          const activity = labels?.get(g.activity_code) ?? g.activity_code;
          const assigned = !!g.project_id;
          const label = `${range}${activity ? ` · ${activity}` : ""}`;
          return (
            <li
              key={`${g.start_minute}-${g.project_id ?? ""}`}
              className={assigned ? undefined : "unassigned"}
            >
              <span className="text">
                <span className="rng">
                  {label}
                  {g.count > 1 &&
                    ` · ${formatMinutes(g.worked_minutes)} en ${g.count} créneaux`}
                </span>
                <span className="prj">
                  {assigned ? (g.project_label ?? "Projet") : "Sans projet"}
                </span>
              </span>
              {editable && g.eventIds.length > 0 && (
                <button
                  type="button"
                  className="pill-btn secondary tap"
                  aria-label={`${assigned ? "Changer de projet" : "Imputer"} ${range}`}
                  onClick={() =>
                    setTargets(
                      g.eventIds.map((eventId) => ({
                        eventId,
                        label,
                        ...(g.project_id ? { projectId: g.project_id } : {}),
                      })),
                    )
                  }
                >
                  {assigned ? "Changer" : "Imputer"}
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {targets && (
        <ReimputeSheet targets={targets} onClose={() => setTargets(null)} />
      )}
    </>
  );
}

export default TimeBlockList;
